<template>
  <v-card class="mb-8">
    <v-card-title>レシートプリンタ</v-card-title>
    <v-card-text>
      <v-text-field
        ref="printIpAddressField"
        :value="form.printIpAddress ? form.printIpAddress.split(':')[0] : ''"
        :error="!form.printIpAddress"
        label="プリンタ IPアドレス"
        placeholder="IPアドレス"
        minlength="7"
        maxlength="15"
        pattern="^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$"
        :rules="[
          value => /^((\d{1,2}|1\d\d|2[0-4]\d|25[0-5])\.){3}(\d{1,2}|1\d\d|2[0-4]\d|25[0-5])$/.test(value) || '無効な値です',
        ]"
        required
        @input="changePrintIpAddress($event)"
      />

      <div class="d-flex align-center flex-wrap mb-4">
        <v-btn
          x-small
          outlined
          color="grey darken-1"
          class="mr-1 mb-1"
          :href="printerWebConfigUrl"
          :disabled="!printerWebConfigUrl"
          target="_blank"
        >
          WebConfig
        </v-btn>
        <v-btn
          x-small
          outlined
          color="warning"
          class="mr-1 mb-1"
          :href="printerCertUrl"
          :disabled="!printerCertUrl"
          target="_blank"
        >
          証明書を受け入れる
        </v-btn>
        <v-btn
          x-small
          outlined
          color="primary"
          class="mr-1 mb-1"
          :loading="checkConnectionStatus.inProgress"
          @click="testConnection"
        >
          接続テスト
        </v-btn>
        <v-slide-x-transition>
          <span
            v-show="checkConnectionStatus.on && !checkConnectionStatus.inProgress"
            class="mr-1"
          >
            <v-icon
              v-if="isConnected"
              small
              color="success"
            >
              {{ icons.mdiCheckCircleOutline }}
            </v-icon>
            <v-icon
              v-else
              small
              color="error"
            >
              {{ icons.mdiCloseCircleOutline }}
            </v-icon>
          </span>
        </v-slide-x-transition>
        <v-btn
          x-small
          outlined
          color="success"
          class="mr-1 mb-1"
          @click="openPrinterDiagnostic"
        >
          診断
        </v-btn>
      </div>
      <printer-diagnostic-dialog />

      <v-divider class="my-4"></v-divider>

      <v-file-input
        ref="receiptPreviewBackgroundImageField"
        v-model="form.receiptPreviewBackgroundImage"
        accept="image/*"
        label="レシートプレビュー背景"
        :rules="[lessThanOrEqualTo2Gb]"
        show-size
      >
        <template #append-outer>
          <v-btn
            color="secondary"
            :ripple="false"
            :disabled="!(currentClub.receiptPreviewBackgroundImage || form.receiptPreviewBackgroundImage)"
            @click="previewBackgroundImage('receiptPreviewBackgroundImage')"
          >
            プレビュー
          </v-btn>
        </template>
      </v-file-input>

      <div class="d-flex justify-end mt-4">
        <v-btn
          color="primary"
          :ripple="false"
          :loading="isSubmitting"
          :disabled="hasError"
          @click="update"
        >
          保存
        </v-btn>
      </div>
    </v-card-text>

    <!-- プレビューダイアログ -->
    <v-dialog
      :value="!!onPreviewKey && !!backgroundImagePreviewUrl"
      max-width="440"
      @input="value => { if (!value) { onPreviewKey = null; backgroundImagePreviewUrl = null } }"
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          レシートプレビュー背景
          <v-btn
            icon
            @click="onPreviewKey = null; backgroundImagePreviewUrl = null"
          >
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="d-flex justify-center">
          <div
            class="preview-bg"
            :style="{ backgroundImage: `url(${backgroundImagePreviewUrl})` }"
          ></div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar
      :value="updated"
      bottom
      right
      vertical
      :timeout="-1"
    >
      <p class="mb-0">
        プリンタIPアドレスの変更を反映するため、リロードしてください
      </p>
      <template #action="{ attrs }">
        <v-btn
          :ripple="false"
          color="primary"
          v-bind="attrs"
          @click="$router.go(0)"
        >
          リロード
        </v-btn>
      </template>
    </v-snackbar>
  </v-card>
</template>

<script>
import {
  ref, reactive, computed, defineComponent, getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiCheckCircleOutline, mdiCloseCircleOutline, mdiPrinterSearch, mdiClose,
} from '@mdi/js'
import { cloneDeep } from 'lodash'
import camelcaseKeys from 'camelcase-keys'
import ClubApi from '@/api/waiter/Club'
import useCurrentData, { setCurrentClub } from '@/views/composable/useCurrentData'
import useReceiptPrinterConnection from '@/views/composable/useReceiptPrinterConnection'
import PrinterDiagnosticDialog from '@/views/components/printer/PrinterDiagnosticDialog.vue'
import { openPrinterDiagnostic } from '@/views/composable/usePrinterDiagnostic'

export default defineComponent({
  components: {
    PrinterDiagnosticDialog,
  },
  setup() {
    const vm = getCurrentInstance().proxy
    const { currentClub } = useCurrentData()
    const { isConnected, checkConnection } = useReceiptPrinterConnection()

    const printIpAddressField = ref(null)
    const receiptPreviewBackgroundImageField = ref(null)
    const isSubmitting = ref(false)
    const onPreviewKey = ref(null)
    const updated = ref(false)
    const backgroundImagePreviewUrl = ref(null)

    // updateClub は club 全フィールドを送信するため、currentClub 全体を form に持って
    // 他項目を保持したまま printIpAddress / プレビュー背景のみ更新する（Club.vue と同じ挙動）
    const form = ref({
      ...cloneDeep(currentClub.value),
      receiptPreviewBackgroundImage: null,
    })

    const checkConnectionStatus = reactive({
      on: false,
      inProgress: false,
    })

    const resetCheckConnectionStatus = () => {
      checkConnectionStatus.on = false
      checkConnectionStatus.inProgress = false
      isConnected.value = false
    }

    const changePrintIpAddress = ipAddress => {
      resetCheckConnectionStatus()
      form.value.printIpAddress = ipAddress
    }

    const testConnection = async () => {
      isConnected.value = false
      checkConnectionStatus.on = true
      checkConnectionStatus.inProgress = true
      // 設定画面の「接続テスト」は端末ごとの無効設定にかかわらず必ず実行する
      await checkConnection(form.value.printIpAddress, { force: true })
      checkConnectionStatus.inProgress = false
    }

    const previewBackgroundImage = key => {
      let url
      if (form.value[key]) {
        url = URL.createObjectURL(form.value[key])
      } else {
        url = currentClub.value[key]
      }

      if (url) {
        backgroundImagePreviewUrl.value = url
        onPreviewKey.value = key
      }
    }

    const lessThanOrEqualTo2Gb = file => {
      return !file || file.size <= (2 * 1024 * 1024 * 1024) || '2GB以下のサイズにしてください'
    }

    const hasError = computed(() => {
      return printIpAddressField?.value?.hasError
        || receiptPreviewBackgroundImageField?.value?.hasError
    })

    const printerCertUrl = computed(() => {
      const ip = form.value?.printIpAddress
      if (!ip) return null
      const cleanIp = ip.split(':')[0]

      return `https://${cleanIp}:8043/cgi-bin/epos/service.cgi`
    })
    const printerWebConfigUrl = computed(() => {
      const ip = form.value?.printIpAddress
      if (!ip) return null
      const cleanIp = ip.split(':')[0]

      return `https://${cleanIp}/`
    })

    const update = async () => {
      isSubmitting.value = true
      // IPアドレスが変わると印刷用の共有接続(Layout provide)が古いIPのままになるためリロードを促す
      const ipChanged = (currentClub.value?.printIpAddress || '') !== (form.value.printIpAddress || '')
      const res = await ClubApi.updateClub(form.value)

      if (res?.data?.club) {
        setCurrentClub(camelcaseKeys(res.data.club))
        vm.$toast.success('更新しました')
        if (ipChanged) updated.value = true
      } else {
        vm.$toast.error('更新できませんでした')
      }

      isSubmitting.value = false
    }

    return {
      form,
      printIpAddressField,
      receiptPreviewBackgroundImageField,
      isSubmitting,
      updated,
      isConnected,
      checkConnectionStatus,
      onPreviewKey,
      backgroundImagePreviewUrl,
      changePrintIpAddress,
      testConnection,
      previewBackgroundImage,
      lessThanOrEqualTo2Gb,
      hasError,
      printerCertUrl,
      printerWebConfigUrl,
      update,
      openPrinterDiagnostic,
      icons: {
        mdiCheckCircleOutline,
        mdiCloseCircleOutline,
        mdiPrinterSearch,
        mdiClose,
      },
    }
  },
})
</script>

<style scoped>
.preview-bg {
  width: 400px;
  height: 70vh;
  background-color: #fff;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
}
</style>
